import React, { useState } from "react";
import moment from "moment";
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

import {
  getTargetData,
  getNutritionPlan,
  isPlanInRange,
} from "../../../../utils/nutrition-helpers";
import { round } from "lodash";

const AdherenceChartNew = ({
  stats,
  dateFrom,
  dateTo,
  averageStats,
  nutritionPlans,
  showAverages,
  activeExercises,
}) => {
  const dateFromUtc = Number(moment(dateFrom).format("YYYYMMDD"));
  const dateToUtc = Number(moment(dateTo).format("YYYYMMDD"));

  const plansInRange = nutritionPlans.filter((plan) =>
    isPlanInRange(plan, dateFromUtc, dateToUtc)
  );

  const statsData = getChartData(stats, plansInRange, false, activeExercises);

  const averageStatsData = getChartData(
    averageStats,
    plansInRange,
    true,
    activeExercises
  );

  return (
    <ResponsiveContainer width="100%">
      <ComposedChart
        key={"adherence-chart-new"}
        data={showAverages ? averageStatsData : statsData}
        margin={{ left: 0 }}
      >
        <CartesianGrid
          stroke="var(--grid-chart-line)"
          strokeDasharray="5 5"
          strokeWidth={1}
          horizontal={true}
          vertical={false}
          margin={{ top: 0, right: 20 }}
        />
        <XAxis
          dataKey="name"
          scale="point"
          tick={{ fill: "var(--white)" }}
          margin={{ top: 4 }}
          tickLine={false}
          dy={5}
        />

        <YAxis
          tick={{ fill: "var(--white)" }}
          axisLine={false}
          tickLine={false}
          dx={-15}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend iconSize={16} iconType="square" spacing={16} />
        <Bar
          name="Fats"
          dataKey="fats"
          stackId="a"
          barSize={20}
          fill="var(--fat)"
        />
        <Bar
          name="Carbs"
          dataKey="carbs"
          stackId="a"
          barSize={20}
          fill="var(--carbs)"
        />
        <Bar
          name="Proteins"
          dataKey="proteins"
          stackId="a"
          barSize={20}
          fill="var(--protein)"
        />
        <Line
          name="Target kCal"
          type="monotone"
          dataKey="target"
          stroke="var(--average-line-chart)"
          strokeDasharray="5 5"
          strokeWidth={1}
          dot={false}
          connectNulls
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

const getChartData = (stats, plansInRange, isWeeksAverage, activeExercises) => {
  // As sorting mutates the original array then we copy the array with a map.
  const statsToSort = stats.map((stat) => stat);

  // Sort the array dates from lower to higher
  const orderedStats = statsToSort.sort((a, b) => a.utcDate - b.utcDate);
  // This will create a map with the necessary data, the output of the object will be similar to:
  // {
  //   name: "May 4",
  //   proteins: 40,
  //   carbs: 10,
  //   fats: 20,
  //   target: 300
  // }
  const statsData = orderedStats.map((stat, i) => {
    const nutritionPlan = getNutritionPlan(
      plansInRange,
      stat.utcDate,
      isWeeksAverage,
      stat.startOfWeek,
      stat.endOfWeek
    );

    const targetData = getTargetData({
      nutritionPlan,
      isPeriod: isWeeksAverage,
      startOfWeek: stat.startOfWeek,
      endOfWeek: stat.endOfWeek,
      averageValues: true,
      activeExercises,
    });

    const proteinsDays = stat.proteinsDays || 1;
    const carbsDays = stat.carbsDays || 1;
    const fatsDays = stat.fatsDays || 1;

    return {
      name: moment(String(stat.utcDate)).format("MMM D"),
      proteins: (stat.nutrients.proteins * 4) / proteinsDays || 0,
      carbs: (stat.nutrients.carbs * 4) / carbsDays || 0,
      fats: (stat.nutrients.fats * 9) / fatsDays || 0,
      target:
        targetData.targetCalories > 0
          ? targetData.targetCalories
          : i === 0
          ? 0
          : null,
    };
  });

  const statsDaysWithPlan = [];

  const statsDataAvg = statsData.reduce(
    //   accumulator will be an object like the following one, storing the average information
    //     {
    //       proteins: 0,
    //       carbs: 0,
    //       fats: 0,
    //       target: 0
    //     }
    (accumulator, currentStat) => {
      // If the stat has a target (meaning a plan) then it will push it to the array
      if (currentStat.target) {
        statsDaysWithPlan.push(currentStat.target);
      }

      return {
        proteins: accumulator.proteins + (currentStat.proteins || 0),
        carbs: accumulator.carbs + (currentStat.carbs || 0),
        fats: accumulator.fats + (currentStat.fats || 0),
        target:
          accumulator.target + (currentStat.target ? currentStat.target : 0),
      };
    },
    // Initial state of the accumulator
    {
      proteins: 0,
      carbs: 0,
      fats: 0,
      target: 0,
    }
  );

  // Get the average object from the previous reducer
  // The target has a failsafe in case there are no plans in that period of time (as it would be a 0)
  const avg = {
    name: "Avg",
    proteins: statsDataAvg.proteins / statsData.length,
    carbs: statsDataAvg.carbs / statsData.length,
    fats: statsDataAvg.fats / statsData.length,
    target: statsDataAvg.target / statsDaysWithPlan.length || 0,
  };

  // So the line starts in 0
  statsData.unshift({ target: 0 });

  // Push the Average for the stats
  statsData.push(avg);

  return statsData;
};

// To show the tooltip with a different style and information
const CustomTooltip = ({ active, payload, label }) => {
  // the condition for the length of the payload is for the first point of the line
  // as that one only has a target of 0, making the lenth 1
  if (active && payload && payload.length > 1) {
    // The payload is an array of objects, the structure would always be like this,
    // except for the first point (as that one is the start of the line)
    // [
    //   {name: "fats", value: 0, ...},
    //   {name: "carbs", value: 0, ...},
    //   {name: "proteins", value: 0, ...},
    //   {name: "Target kCal", value: 0, ...}
    // ]

    const consumed = payload[0].value + payload[1].value + payload[2].value;

    // The target will always be in the third index of the array
    const isTarget = Boolean(payload[3]);

    return (
      <div className="custom-tooltip">
        <span className="mx-auto">Macros</span>
        <div>
          <span className="protein">Protein</span>
          <span>{round(payload[2].value / 4, 0)}g</span>
        </div>
        <div>
          <span className="carbs">Carbs: </span>
          <span>{round(payload[1].value / 4, 0)}g</span>
        </div>
        <div>
          <span className="fat">Fat</span>
          <span>{round(payload[0].value / 9, 0)}g</span>
        </div>
        <div>
          <span>Consumed kCal</span>
          <span>{round(consumed, 0)}</span>
        </div>

        {isTarget && (
          <div>
            <span>Target kCal</span>
            <span>{round(payload[3].value, 0)}</span>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default AdherenceChartNew;
